// @givmo/compliance — sending a signed filing: the send review, its outcomes, and what a person
// records about an uncertain send (CCM-R1-SUBMIT-WEB; UX slice 3 §5–§8 and §11, frames 62–73,
// approved under D-CCM-27).
//
// `/compliance/filings/<filing id>/send`. The case is the one the form is for: the filing's open
// case for the year (or the filing made once) its next date names — the workbench's own target.
// Where no case is open, or sending is dark here (the read's 404), the address goes back to the
// filing's panel.
//
// ══ WHAT THE PAGE IS ═══════════════════════════════════════════════════════════════════════════
//
// The left column is what goes, exactly as it goes: the package's files and the email's words, as
// the server composes them. Once an authorization binds them, it is what was bound and, once sent,
// the addresses the send recorded, so the column keeps showing what went. The right column is the
// decision, then the record: "Your authorization" with its rows, the reply contact and the act's
// words over **Authorize and send** — which authorizes inside the sign-in check and sends at once,
// one press (the sign-in check covers sending, D-CCM-27 item 4) — then, once sending starts, the
// record of what happened: sending, sent, not sent, outcome uncertain (never sent again by itself:
// a person records what they found), or recorded as not sent.
//
// ══ THE SIGN-IN CHECK, AND A PASSKEY (CCM-R1-SIGN-PASSKEY) ═════════════════════════════════════
//
// Authorize and send runs through the ONE "Confirm it's you" (`compliance-signing-gate.tsx`), so a
// person holding a passkey meets a stricter level with the sheet's passkey arm (Account Security's
// SEC2-AUTHWEB). An account that holds nothing the sheet could use — the gate's typed
// `StepUpPasskeyFailure('none_registered')`, decided on the account read the gate makes at the
// refusal — reads W1 above the act and gets the host's offer to set up a passkey in the button's
// place, never "Please try again", which no retry would cure. This page reads no factor of its own
// (AS-WEB15): it learns that from the refused press, which authorizes and sends nothing. After a
// passkey is set up, the button comes back and nothing runs by itself: a send leaves Givmo for the
// state, so it waits for the person's own press. An offer that settles `unavailable` (this browser
// or this environment can't create a passkey) shuts the act with W1 standing, as when the host has
// no offer at all: a press could only meet the same refusal again.
//
// ══ THE ORGANIZATION'S AI (CCM-R1-SUBMIT-MCP; frames 62, 74–77) ══════════════════════════════
//
// A person with an AI connected to this organization that may write its Compliance records (the
// host's `connectedAiCanSend`, never Givmo's AI setting: UX slice 3's P6) sees "Let your AI send
// this" beside "Send it yourself instead". It swaps only the act block (frame 74): **Authorize my
// AI to send it** authorizes by frame 74's words inside the same sign-in check and sends NOTHING;
// "Send it myself instead" swaps back. The authorization then reads "Authorized for your AI"
// (frame 75) to its author while their AI may send it: **Send it now** sends it from here instead,
// and Revoke asks first (frame 76), because it takes the AI's allowance away. Anyone else reads
// whose AI may send it (W-a). The AI's send reads as its own (frame 77): by whom, and what the
// person asked their AI as the AI reported it — the organization's own account, which Givmo
// doesn't check.
//
// ══ SENDING IT YOURSELF (CCM-R1-SUBMIT-HANDOFF) ════════════════════════════════════════════════
//
// The same address shows the handoff (frames 79–80, `filing-handoff.tsx`) where Givmo won't email
// the filing, where the organization chose "Send it yourself instead" (the `view=yourself` search),
// and once it recorded what it sent. Chosen for a filing Givmo can email, it ends step 2 with the
// way back, "Have Givmo email it instead" (N-H1, D-CCM-40), which opens the review. The review
// carries the agency's own sentences (the To row's quote, the letters line, What happens next's
// quotes), each served, cited words.
//
// ══ WHERE THE ADDRESS COMES FROM (CCM-ST2-SUBMIT-CITE) ═════════════════════════════════════════
//
// The To row cites the source the rule's address was read from, as the backend serves it
// (`destination.citation`): its citation in the line's own slot, the page, the agency's sentence,
// the date worded by its basis (and the possible-change sentence, as every source line adds it), and
// "Official source" to that source. Where none is served (an older backend, or a rule that cites
// none yet), the row keeps the line it drew before, from the form's printed words and the date its
// template was taken. The two are never mixed in one line.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Button,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import type {
  ComplianceSubmissionAuthorize,
  ComplianceSubmissionEvidenceAnswer,
  ComplianceSubmissionEvidenceRecord,
} from '@givmo/api-client';
import { isStepUpCancelled } from '@givmo/step-up';
import { Building2, Download } from 'lucide-react';
import { useCompliance } from './compliance-context';
import {
  displayComplianceDate,
  extractArtifacts,
  isModuleUnavailable,
  serverMessage,
} from './compliance-decode';
import { libraryNextDate, ruleShapesOf, withChosenCycle } from './compliance-filings-model';
import {
  chosenYearSearch,
  useChosenCycleId,
  withChosenYear,
  useFilingsComposition,
} from './compliance-filings-reads';
import {
  isYearKeyed,
  longComplianceDate,
  periodLineOf,
  receiptKindFor,
} from './compliance-panel-model';
import {
  adapterForRule,
  decodeChecklist,
  decodeFormPreparation,
  extractFilingCases,
  extractFormAdapters,
  extractPreparationSummaries,
  type FormItem,
  type SourceField,
} from './compliance-preparation-decode';
import { caseForTarget, makerName, preparationTargetFor } from './compliance-preparation-model';
import {
  casePreparationsQueryKey,
  filingCasesQueryKey,
  filingChecklistQueryKey,
  formAdaptersQueryKey,
  formPreparationQueryKey,
  listAllArtifacts,
  listAllCasePreparations,
  listAllFilingCases,
  listAllFormAdapters,
  signingRequestQueryKey,
  submissionQueryKey,
} from './compliance-queries';
import {
  COMPLIANCE_PAGE_TEST_IDS,
  COMPLIANCE_ROUTES,
  SEND_AGAIN_SEARCH,
  SEND_YOURSELF_SEARCH,
} from './compliance-routes';
import {
  ATTACH_THE_CONFIRMATION,
  PREPARE_THE_CORRECTED_FILING,
  PREPARE_THE_CORRECTED_FILING_FIRST,
  PREPARE_THE_CORRECTED_FILING_LINE,
  REPORTED_AS_SENT,
  SEND_IT_AGAIN,
  YOU_CAN_SEND_IT_AGAIN,
  deliveryListRows,
  evidenceAbout,
  followThroughOf,
  itDidntReachLabel,
  knownNowLine,
  laterReportLine,
  nextStepOf,
  receivingIsntAcceptingLine,
  recordAnswerLabel,
  sendsThatWentOut,
  standingEvidence,
  takingReports,
  type ShownSend,
} from './compliance-results-model';
import {
  ModuleUnavailableNotice,
  STICKY_COLUMN_CLASSES,
  sourceChangeWording,
  sourceDateWording,
} from './compliance-shared';
import { failedForWantOfAPasskey, useCannotMeetCheck } from './cannot-meet-check';
import { useComplianceActGate } from './compliance-signing-gate';
import { decodeSigningRequestDetail } from './compliance-signing-decode';
import {
  cannotMeetCheckToSendLine,
  organizationOf,
  shortDateIn,
  whenOf,
} from './compliance-signing-model';
import {
  SUBMISSION_REFUSALS,
  decodeSubmission,
  decodeSubmissionPackageFile,
  sentByConnector,
  type PersonReconciliationBasis,
  type SubmissionAttempt,
  type SubmissionAuthorization,
  type SubmissionBoundEntry,
  type SubmissionEvidence,
  type SubmissionFollowThrough,
} from './compliance-submission-decode';
import {
  AUTHORIZATION,
  AUTHORIZED_FOR_YOUR_AI,
  AUTHORIZE_AND_SEND,
  AUTHORIZE_MY_AI_TO_SEND_IT,
  BACK_TO_THE_FILING,
  CARD_ROW_LABELS,
  DOWNLOAD_THE_PACKAGE,
  EVERY_ITEM_LINE,
  HAVE_GIVMO_EMAIL_IT_INSTEAD,
  IF_IT_HAPPENS_AGAIN,
  LET_YOUR_AI_SEND_THIS,
  NOT_SENT,
  ONLY_OWNERS_RECORD,
  OFFICIAL_SOURCE,
  OUTCOME_UNCERTAIN,
  SEE_THE_CHECKLIST,
  SEND_IT_MYSELF_INSTEAD,
  SEND_IT_NOW,
  SEND_IT_YOURSELF_INSTEAD,
  SEND_IT,
  SENDING_RECORD,
  TRYING_AGAIN_LINE,
  TRY_AGAIN,
  WHAT_HAPPENS_NEXT,
  WHAT_YOU_TELL_YOUR_AI,
  YOUR_AUTHORIZATION,
  YOUR_SIGN_IN_EMAIL,
  YOU_CAN_SEND_IT_NOW,
  acceptedLine,
  aiAskedLine,
  aiAuthorizedByLine,
  aiAuthorizedOnLine,
  aiCanSendLine,
  aiStartedLine,
  aiWorksUntilLine,
  authorizedForYourAiLine,
  authorizedRecordLine,
  capacityLine,
  checklistCountsLine,
  checklistRecordCounts,
  didItArriveLine,
  digestsRecordLine,
  enteredLine,
  feeValue,
  findingDialog,
  handoffLeadReason,
  isOutcome,
  lettersLine,
  newestHandoff,
  onlyOwnersAuthorizeLine,
  organizationEmailLabel,
  panelSendAction,
  recipientsRecordLine,
  reconciledSentLine,
  recordedNeverArrivedLine,
  refusedLine,
  repliesGoTo,
  repliesGoToLine,
  repliesGoToSignInLine,
  sendToTitle,
  recordedSendDenial,
  sendingRecordDenial,
  sendingTitle,
  sentByAiLine,
  sentByConnectedAiLine,
  sentByEmailLine,
  sentFromGivmoLine,
  sentTitle,
  sentToSinkByAiLine,
  sentToSinkLine,
  showsHandoff,
  signedByLine,
  sinkLine,
  startedLine,
  stillOpenLine,
  subjectHintLine,
  submissionStage,
  toRowCitationLine,
  toRowQuoteLine,
  uncertainLine,
  whatHappensNextLine,
  worksUntilLine,
  type SubmissionStage,
} from './compliance-submission-model';
import {
  ActSection,
  AiAskedBox,
  AuthorizationCard,
  CardRows,
  CardSection,
  FindingDialog,
  MemberSection,
  RecordCard,
  RecordLines,
  ReplyChoice,
  RevokeForAiDialog,
  SendAgainActs,
  SendingRecord,
  SendingSection,
  UncertainQuestion,
  WhatHappensNext,
  type CardRow,
  type ReplyChoiceOption,
} from './filing-send-cards';
import {
  SendEmailSection,
  SendPackageSection,
  SourceLine,
  type ShownEmail,
  type ShownFile,
} from './filing-send-parts';
import { HandoffBody, type HandoffRecordBody, type HandoffSigner } from './filing-handoff';
import { RecordFilingForm, type RecordTarget } from './filing-panel-forms';
import {
  AnswerDialog,
  DeliveryList,
  RecordAnswerDialog,
  ResultsAct,
  type AnswerTarget,
} from './filing-send-results';
import { useSignedOutside } from './signed-outside';
import {
  decodeDeskCase,
  deskHolds,
  deskStatusLine,
  needsAuthorization,
} from './compliance-desk-model';
import { AUTHORIZE, N11, N12, N13, N9, W3C_NEED, W4, n10, w3 } from './desk-words';
import { Trail } from './filing-workbench';
import { QuietButton } from './filing-workbench-pages';
import { useOpenVersion } from './signing-shared';

const PREFIX = 'compliance-send';

/**
 * The To row's "Official source", after its line: the source the line cites, opened in a new tab,
 * which a screen reader is told (review-61's N2).
 */
function officialSourceLink(href: string): ReactNode {
  return (
    <>
      {' '}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-text-secondary"
        {...testIdProp(`${PREFIX}-official-source`)}
      >
        {OFFICIAL_SOURCE} <span className="sr-only">(opens in a new tab)</span>
      </a>
    </>
  );
}

/** The line every failed read of this page says, as the workbench says it. */
const LOAD_FAILED = 'We couldn’t load this filing. Please try again.';

/** The line a failed act says when the server gave none. */
const TRY_AGAIN_LINE = 'Please try again.';

/** A send under way from this page: when it started, and who pressed (where the page knows). */
interface InFlight {
  at: string;
  byUserId?: string;
  label: string;
}

/** The form's own value for the item that reads one record field (its name, its EIN). */
function formValue(items: readonly FormItem[], field: SourceField): string | undefined {
  const item = items.find((candidate) => candidate.reads.some((read) => read.field === field));
  const value = item?.values.find((line) => line.trim().length > 0);
  return value?.trim();
}

/** Whether the form's email answer came from the organization's profile, not typed (G2, M2). */
function emailFromProfile(items: readonly FormItem[]): boolean {
  const item = items.find((candidate) => candidate.reads.some((read) => read.field === 'email'));
  if (item === undefined || item.answer?.kind === 'value') return false;
  return item.sources.some(
    (source) => source.field === 'email' && source.kind === 'organization_profile',
  );
}

function fromLine(displayName: string, address: string): string {
  return displayName.length > 0 ? `${displayName} <${address}>` : address;
}

/** The form's checklist page as the preparation read serves it (`checklist.page`; MH9). */
function servedChecklistPage(raw: unknown): number | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const checklist = (raw as { checklist?: unknown }).checklist;
  if (typeof checklist !== 'object' || checklist === null) return undefined;
  const page = (checklist as { page?: unknown }).page;
  return typeof page === 'number' && Number.isInteger(page) ? page : undefined;
}

export function FilingSendPage() {
  const {
    orgRef,
    isReady,
    isError: orgError,
    error: orgFailure,
    operations,
    moduleStanding,
    personName,
    organizationName,
    canSendForSignature,
    complianceAuthorizers,
    connectedAiCanSend,
    canWrite,
    viewerUserId,
  } = useCompliance();
  const { filingId = '' } = useParams({ strict: false }) as { filingId?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openVersion = useOpenVersion();
  const gate = useComplianceActGate();
  const available = moduleStanding === 'available';
  const ref = isReady && available ? orgRef : null;
  const composition = useFilingsComposition(operations, ref);
  const records = composition.records;
  const obligation = records?.obligations.find((candidate) => candidate.obligationId === filingId);
  const row = composition.rows?.find((candidate) => candidate.ref.id === filingId);

  const chosenCycleId = useChosenCycleId();
  const yearSearch = chosenYearSearch(chosenCycleId);
  const next = useMemo(() => {
    if (obligation === undefined || records === undefined) return undefined;
    const cycles = records.cycles.filter((cycle) => cycle.obligationId === obligation.obligationId);
    const occurrences = records.occurrences.filter(
      (occurrence) => occurrence.obligationId === obligation.obligationId,
    );
    // CCM-R1-LATE-FILINGS (D-CCM-62): the year it was opened for (`?cycle=`), else the next date's.
    return withChosenCycle(
      libraryNextDate(
        obligation,
        cycles,
        occurrences,
        records.profile,
        records.today,
        ruleShapesOf(records.ruleShapes, obligation),
      ),
      cycles,
      chosenCycleId,
    );
  }, [obligation, records, chosenCycleId]);
  const target = useMemo(
    () => (next !== undefined ? preparationTargetFor(next) : undefined),
    [next],
  );

  const adapters = useQuery({
    queryKey: formAdaptersQueryKey(ref),
    queryFn: () => listAllFormAdapters(operations, ref ?? ''),
    enabled: ref !== null,
  });
  const cases = useQuery({
    queryKey: filingCasesQueryKey(ref, obligation?.obligationId),
    queryFn: () => listAllFilingCases(operations, ref ?? '', obligation?.obligationId ?? ''),
    enabled: ref !== null && obligation !== undefined,
  });
  const openCase =
    target !== undefined ? caseForTarget(extractFilingCases(cases.data), target) : undefined;
  const caseId = openCase?.caseId;
  const adapter =
    obligation !== undefined
      ? adapterForRule(
          extractFormAdapters(adapters.data),
          obligation.jurisdictionCode,
          obligation.ruleId,
        )
      : undefined;
  const preparations = useQuery({
    queryKey: casePreparationsQueryKey(ref, caseId),
    queryFn: () => listAllCasePreparations(operations, ref ?? '', caseId ?? ''),
    enabled: ref !== null && caseId !== undefined,
  });
  const summary = extractPreparationSummaries(preparations.data)
    .filter((candidate) => adapter === undefined || candidate.adapterKey === adapter.adapterKey)
    .at(-1);
  const preparationRead = useQuery({
    queryKey: formPreparationQueryKey(ref, summary?.preparationId),
    queryFn: () => operations.getFormPreparation(ref ?? '', summary?.preparationId ?? ''),
    enabled: ref !== null && summary !== undefined,
  });
  const checklist = useQuery({
    queryKey: filingChecklistQueryKey(ref, caseId),
    queryFn: () => operations.getFilingCaseChecklist(ref ?? '', caseId ?? ''),
    enabled: ref !== null && caseId !== undefined,
  });
  const submissionKey = submissionQueryKey(ref, caseId);
  const submissionRead = useQuery({
    queryKey: submissionKey,
    queryFn: () => operations.getSubmission(ref ?? '', caseId ?? ''),
    enabled: ref !== null && caseId !== undefined,
  });
  const submission = decodeSubmission(submissionRead.data);
  // CCM-R1-DESK-WEB: Givmo's filing desk on this case, as served. While it has the filing, the
  // organization's own ways to send are hidden (Q12) and the desk's own line says what's next.
  const desk = decodeDeskCase(submissionRead.data);
  const deskHeld = deskHolds(desk);
  // The documents' own names, for the list's records (CCM-R1-RESULTS-WEB): read only where one is.
  const vault = useQuery({
    queryKey: ['compliance', 'artifacts', ref],
    queryFn: () => listAllArtifacts(operations, ref ?? ''),
    enabled: ref !== null && (submission?.evidence.some((row) => row.artifactId) ?? false),
  });
  const artifactName = (artifactId: string) =>
    extractArtifacts(vault.data).find((artifact) => artifact.artifactId === artifactId)?.fileName;
  // "Send it yourself instead" keeps its choice in the address (CCM-R1-SUBMIT-HANDOFF).
  const search = useSearch({ strict: false }) as { view?: unknown };
  // Not while Givmo's desk has the filing (Q12), even by an address kept from before.
  const yourself = search.view === SEND_YOURSELF_SEARCH.view && !deskHeld;
  const again = search.view === SEND_AGAIN_SEARCH.view;
  const requestId = submission?.signature?.requestId;
  const signedRead = useQuery({
    queryKey: signingRequestQueryKey(ref, requestId),
    queryFn: () => operations.getSigningRequest(ref ?? '', requestId ?? ''),
    enabled: ref !== null && requestId !== undefined,
  });
  const signed = decodeSigningRequestDetail(signedRead.data);
  // CCM-R1-SIGN-OUTSIDE-SEND-B: a form whose execution is a copy signed outside Givmo is sent from
  // here as a signed form is (frames 111 and 112), the copy named by the read's own `execution`.
  // A server older than that never sends a copy and serves no `execution`: there a form with a copy
  // and nothing signed in Givmo goes back to its workbench, which says what is true of it (frame
  // 87), so this page never shows "Every form … must be signed" beside a copy the person just
  // recorded (review-111's P2).
  const copyRead = useSignedOutside(decodeFormPreparation(preparationRead.data), {
    withPages: false,
  });
  const copyOnly =
    copyRead.outside !== undefined &&
    requestId === undefined &&
    !submissionRead.isPending &&
    submission?.execution === undefined;
  useEffect(() => {
    if (copyOnly)
      void navigate({
        to: COMPLIANCE_ROUTES.filingForm(filingId),
        ...withChosenYear(chosenCycleId),
        replace: true,
      });
  }, [copyOnly, navigate, filingId, chosenCycleId]);

  const [replySource, setReplySource] = useState<string | undefined>(undefined);
  const [inFlight, setInFlight] = useState<InFlight | null>(null);
  const [againFailure, setAgainFailure] = useState<unknown>(null);
  // W1 and the passkey offer: set when the gate found nothing on this account that can meet the
  // organization's sign-in check here (the header; `cannot-meet-check.tsx`, shared with the level
  // card).
  const passkeyCheck = useCannotMeetCheck('send');
  const [finding, setFinding] = useState<
    { attemptId: string; basis: PersonReconciliationBasis } | undefined
  >(undefined);
  // Frame 74's act block in place of frame 62's, and frame 76's dialog over the authorized card.
  const [aiView, setAiView] = useState(false);
  const [revoking, setRevoking] = useState<SubmissionAuthorization | undefined>(undefined);
  // What came back (CCM-R1-RESULTS-WEB): recording the agency's answer (c1), saying a report or a
  // record is wrong (d1, d2), and Attach the confirmation's form in the card.
  const [recordingAnswer, setRecordingAnswer] = useState(false);
  const [answering, setAnswering] = useState<AnswerTarget | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);

  // Sending is dark here, or the read is gone: the panel is where the filing stands.
  const submissionMissing =
    submissionRead.error !== null &&
    submissionRead.error !== undefined &&
    isModuleUnavailable(submissionRead.error);
  const reads = [adapters, cases, preparations, preparationRead, checklist];
  const failed =
    composition.failed ??
    reads.find((read) => read.error !== null && read.error !== undefined)?.error ??
    (!submissionMissing && submissionRead.error !== null ? submissionRead.error : undefined) ??
    (requestId !== undefined && signedRead.error !== null ? signedRead.error : undefined);
  const settled =
    !composition.loading &&
    !adapters.isPending &&
    (obligation === undefined || !cases.isPending) &&
    (caseId === undefined ||
      (!preparations.isPending && !checklist.isPending && !submissionRead.isPending)) &&
    (summary === undefined || !preparationRead.isPending) &&
    (requestId === undefined || !signedRead.isPending);
  // CCM-R1-WORKBENCH-PREPARE-SETTLE, the workbench's guard: no case is decided on a fresh read alone,
  // never on a case list cached from before an act and being read again. No way in reaches this page
  // so today (each comes from a page that has read the case); the guard keeps the next one from it.
  const nowhere =
    settled &&
    !cases.isFetching &&
    failed === undefined &&
    records !== undefined &&
    (obligation === undefined || caseId === undefined || submissionMissing);
  useEffect(() => {
    if (nowhere) void navigate({ to: COMPLIANCE_ROUTES.filing(filingId), replace: true });
  }, [nowhere, navigate, filingId]);

  /** A send changed the case: its read takes the act's own answer, and the register reads again. */
  const settle = (answer: unknown) => {
    if (decodeSubmission(answer) !== undefined) queryClient.setQueryData(submissionKey, answer);
    // The register's mirror records a send (the panel's dated line), so every read refreshes.
    void queryClient.invalidateQueries({ queryKey: ['compliance'] });
  };
  const reread = () => void queryClient.invalidateQueries({ queryKey: submissionKey });

  // Authorize and send: the authorization inside the sign-in check, then the send it numbers, as
  // one press. A refused send after a good authorization leaves it standing (M4): Send it. A second
  // press in the same tick, before the busy state renders, is ignored (the backend converges a
  // repeat too: the same echoes answer the same authorization, the same number the same send).
  const acting = useRef(false);
  const authorizeAndSend = useMutation({
    mutationFn: async (body: ComplianceSubmissionAuthorize) => {
      const authorized = await gate.run(() =>
        operations.authorizeSubmission(ref ?? '', caseId ?? '', body),
      );
      queryClient.setQueryData(submissionKey, authorized);
      const authorization = decodeSubmission(authorized)?.authorization;
      // CCM-R1-DESK-WEB (desk-09): an authorization for Givmo's desk to send is the desk's to
      // send, never this press's (the organization's own send is refused while the desk has it).
      if (
        authorization === undefined ||
        authorization.state !== 'active' ||
        authorization.mode === 'givmo'
      ) {
        return { answer: authorized };
      }
      setInFlight({
        at: new Date().toISOString(),
        byUserId: authorization.authorizedByUserId,
        label: AUTHORIZE_AND_SEND,
      });
      try {
        return {
          answer: await operations.dispatchSubmission(ref ?? '', authorization.authorizationId, {
            expected_attempt_ordinal: authorization.nextAttemptOrdinal,
          }),
        };
      } catch (cause) {
        return { dispatchFailed: cause };
      }
    },
    onSuccess: (outcome) => {
      if ('answer' in outcome) {
        setAgainFailure(null);
        settle(outcome.answer);
      } else {
        setAgainFailure(outcome.dispatchFailed);
        reread();
      }
      // Sent again (a3): the address leaves the review it opened, so the page shows this send.
      if (again)
        void navigate({
          to: COMPLIANCE_ROUTES.filingSend(filingId),
          ...withChosenYear(chosenCycleId),
          replace: true,
        });
    },
    onError: (error) => {
      // Nothing on this account can meet the check here, and nothing was authorized or sent: W1,
      // and the offer to set up a passkey (the header).
      if (passkeyCheck.caught(error)) return;
      reread();
    },
    onSettled: () => {
      acting.current = false;
      setInFlight(null);
    },
  });

  // Authorize my AI to send it (frame 74): the authorization inside the same sign-in check, by
  // frame 74's words — and nothing is sent: the person's AI sends it, or they do (frame 75).
  const authorizeForAi = useMutation({
    mutationFn: (body: ComplianceSubmissionAuthorize) =>
      gate.run(() => operations.authorizeSubmission(ref ?? '', caseId ?? '', body)),
    onSuccess: (answer) => {
      setAiView(false);
      settle(answer);
    },
    onError: (error) => {
      if (passkeyCheck.caught(error)) return;
      reread();
    },
    onSettled: () => {
      acting.current = false;
    },
  });

  // Send it / Try again: the next send under the authorization in force.
  const sendAgain = useMutation({
    mutationFn: ({
      authorization,
      label,
    }: {
      authorization: SubmissionAuthorization;
      label: string;
    }) => {
      setInFlight({ at: new Date().toISOString(), label });
      return operations.dispatchSubmission(ref ?? '', authorization.authorizationId, {
        expected_attempt_ordinal: authorization.nextAttemptOrdinal,
      });
    },
    onSuccess: (answer) => {
      setAgainFailure(null);
      settle(answer);
    },
    onError: (cause) => {
      setAgainFailure(cause);
      reread();
    },
    onSettled: () => setInFlight(null),
  });

  // A person's Revoke, in one click (the GO's Q5): it only takes authority away. Over an
  // authorization its author's AI may still send, it asks first (frame 76; cut 1006-9's Q7).
  const revoke = useMutation({
    mutationFn: (authorization: SubmissionAuthorization) =>
      operations.revokeSubmissionAuthorization(ref ?? '', authorization.authorizationId),
    onSuccess: (answer) => {
      setAgainFailure(null);
      setRevoking(undefined);
      settle(answer);
    },
    onError: (cause) => {
      setAgainFailure(cause);
      reread();
    },
  });

  // What a person found about an uncertain send; nothing is sent again.
  const reconcile = useMutation({
    mutationFn: (chosen: { attemptId: string; basis: PersonReconciliationBasis }) =>
      operations.reconcileSubmissionAttempt(ref ?? '', chosen.attemptId, {
        basis: chosen.basis,
        note: null,
      }),
    onSuccess: (answer) => {
      setFinding(undefined);
      settle(answer);
    },
    onError: reread,
  });

  // Record what the organization sent itself (frame 80); Save goes back to the filing (frame 81).
  const recordHandoff = useMutation({
    mutationFn: (body: HandoffRecordBody) =>
      operations.recordSubmissionHandoff(ref ?? '', caseId ?? '', body),
    onSuccess: (answer) => {
      settle(answer);
      void navigate({ to: COMPLIANCE_ROUTES.filing(filingId) });
    },
    onError: reread,
  });

  // Record the agency's answer (c1): in the recorder's name, beside the sending record.
  const recordEvidence = useMutation({
    mutationFn: (body: ComplianceSubmissionEvidenceRecord) =>
      operations.recordSubmissionEvidence(ref ?? '', caseId ?? '', body),
    onSuccess: (answer) => {
      setRecordingAnswer(false);
      settle(answer);
    },
    onError: reread,
  });

  // Say a report or a record is wrong (d1, d2): each row the answer is about, in turn. A "Reported
  // as sent" card's every standing report that the agency's address took it is answered, so
  // nothing counts the send as taken any more.
  const answerEvidence = useMutation({
    mutationFn: async ({
      evidenceIds,
      body,
    }: {
      evidenceIds: readonly string[];
      body: ComplianceSubmissionEvidenceAnswer;
    }) => {
      let answer: unknown;
      for (const evidenceId of evidenceIds) {
        answer = await operations.answerSubmissionEvidence(
          ref ?? '',
          caseId ?? '',
          evidenceId,
          body,
        );
      }
      return answer;
    },
    onSuccess: (answer) => {
      setAnswering(undefined);
      settle(answer);
    },
    onError: reread,
  });

  // Download the package: the files as they go, or as the authorization bound them.
  const download = useMutation({
    mutationFn: async (digest: string) => {
      const file = decodeSubmissionPackageFile(
        await operations.getSubmissionPackage(ref ?? '', caseId ?? '', digest),
      );
      if (file === undefined) throw new Error(TRY_AGAIN_LINE);
      return file.downloadUrl;
    },
    // Each link is spent here: the next click asks for a fresh one, and the server logs each.
    onSuccess: (url) => window.location.assign(url),
    onError: (cause) => {
      toast.error(serverMessage(cause, TRY_AGAIN_LINE));
      reread();
    },
  });

  const jurisdiction =
    submission?.destination?.jurisdictionName ||
    obligation?.jurisdictionName ||
    obligation?.jurisdictionCode ||
    '';
  const zone = signed?.version.timeZone;
  // The desk's status line (D-CCM-67's W1–W8), W5's day from the desk's send the service took.
  const deskSent = submission?.attempts
    .filter((each) => each.dispatchedThrough === 'desk' && each.state === 'provider_accepted')
    .at(-1);
  const deskLine = deskStatusLine(
    desk?.assignment,
    deskSent?.committedAt !== undefined ? shortDateIn(deskSent.committedAt, zone) : undefined,
  );
  const preparation = decodeFormPreparation(preparationRead.data);
  const items = preparation?.items ?? [];
  const organization = formValue(items, 'name') ?? organizationOf(organizationName);
  const ein = formValue(items, 'ein');
  const filingTitle =
    row?.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : (row?.headline ?? '');
  const periodEnd = target?.kind === 'renewal' ? target.cycle.periodEnd : undefined;
  const yearKeyed =
    periodEnd !== undefined &&
    target?.kind === 'renewal' &&
    !target.cycle.cycleKey.startsWith('standing');
  const periodLine =
    yearKeyed && periodEnd !== undefined
      ? `For the fiscal year ended ${longComplianceDate(periodEnd)}`
      : undefined;
  const signedFormTitle = submission?.package?.entries.find(
    (entry) => entry.role === 'signed_form',
  )?.title;

  const sendStage: SubmissionStage | undefined =
    submission !== undefined ? submissionStage(submission) : undefined;
  // What came back about the send the page shows (CCM-R1-RESULTS-WEB, Q1: the newest).
  const sentAttempt =
    sendStage !== undefined && 'attempt' in sendStage ? sendStage.attempt : undefined;
  const sentFollowThrough =
    submission !== undefined && sentAttempt !== undefined
      ? followThroughOf(submission, { attemptId: sentAttempt.attemptId })
      : undefined;
  // a3, a4: an email the agency's address never got goes again only by a person's new act (D-CCM-68):
  // Send it again opens the review, and Send it yourself instead the handoff.
  const goingAgain =
    sendStage?.kind === 'sent' &&
    sentFollowThrough?.state === 'not_delivered' &&
    (again || yourself);
  const stage: SubmissionStage | undefined = goingAgain ? { kind: 'review' } : sendStage;
  // Sending it yourself (CCM-R1-SUBMIT-HANDOFF): where Givmo won't email it, where the person chose
  // it, or once the organization recorded what it sent.
  const handoffShown =
    submission !== undefined &&
    (goingAgain
      ? yourself && submission.instructions !== undefined
      : showsHandoff(submission, yourself));
  const authorization =
    stage !== undefined && 'authorization' in stage ? stage.authorization : undefined;
  const attempt = stage !== undefined && 'attempt' in stage ? stage.attempt : undefined;
  // A Member, as the host reports it; unreported, the server's own answer on the review (ready, and
  // no authorize ability). Never read off the abilities once an authorization exists: then nobody
  // may authorize, whoever they are.
  const member =
    canSendForSignature === false ||
    (canSendForSignature === undefined &&
      stage?.kind === 'review' &&
      submission !== undefined &&
      submission.ready &&
      !submission.abilities.authorize);

  // ── The files and the email the page shows ──────────────────────────────────────────────────
  const checklistItems = decodeChecklist(checklist.data);
  const quoteFor = (index: number | undefined) =>
    index !== undefined
      ? checklistItems.find((item) => item.attachmentIndex === index)?.agencyQuote
      : undefined;
  // An item's quote goes under its first file only: frame 79 quotes Maryland's Schedule B line
  // under the Form 990, never again under the Form 8879-EO that answers the same item.
  const quoted = new Set<number>();
  const composedFiles: ShownFile[] = (submission?.package?.entries ?? []).map((entry) => {
    const index = entry.checklistItemIndex;
    const quote = index !== undefined && !quoted.has(index) ? quoteFor(index) : undefined;
    if (index !== undefined) quoted.add(index);
    return {
      key: entry.versionId,
      documentId: entry.documentId,
      versionId: entry.versionId,
      title: entry.title,
      role: entry.role,
      ...(entry.checklistItem !== undefined ? { checklistItem: entry.checklistItem } : {}),
      fileName: entry.fileName,
      pageCount: entry.pageCount,
      byteSize: entry.byteSize,
      contentSha256: entry.contentSha256,
      ...(quote !== undefined ? { agencyQuote: { text: quote.text } } : {}),
    };
  });
  // A bound package (an authorization's, or one the organization recorded sending), with what the
  // read still says about each of its files.
  const fromBound = (entries: readonly SubmissionBoundEntry[]): ShownFile[] =>
    entries.map((entry) => {
      const known = composedFiles.find((file) => file.versionId === entry.versionId);
      return {
        key: entry.versionId,
        documentId: entry.documentId,
        versionId: entry.versionId,
        title: known?.title ?? entry.fileName,
        role: entry.role,
        ...(known?.checklistItem !== undefined ? { checklistItem: known.checklistItem } : {}),
        fileName: entry.fileName,
        ...(known?.pageCount !== undefined ? { pageCount: known.pageCount } : {}),
        byteSize: entry.byteSize,
        contentSha256: entry.contentSha256,
        ...(known?.agencyQuote !== undefined ? { agencyQuote: known.agencyQuote } : {}),
      };
    });
  // Once bound, the column shows what was bound: the composed package where it is the same one,
  // else the bound files with what the read still says about each.
  const files: ShownFile[] =
    authorization === undefined ||
    submission?.package?.packageSha256 === authorization.packageSha256
      ? composedFiles
      : fromBound(authorization.package);
  const totalBytes = files.reduce((sum, file) => sum + file.byteSize, 0);
  // The not-needed count: today's checklist until an authorization keeps its own; then the
  // authorization's, and none where it kept none (CCM-R1-SUBMIT-SENT-SUMMARY).
  const todaysNotNeeded = checklistItems.filter((item) => item.status === 'not_needed').length;
  const keptChecklist =
    authorization !== undefined && authorization.checklist !== undefined
      ? checklistRecordCounts(authorization.checklist)
      : undefined;
  const notNeeded =
    authorization === undefined
      ? todaysNotNeeded
      : keptChecklist !== undefined
        ? keptChecklist.notNeeded
        : undefined;
  const packageDigest = authorization?.packageSha256 ?? submission?.package?.packageSha256;

  // The reply contact: chosen here before the act; the bound one after it. The read offers each
  // viewer their own sign-in email; a Member can't authorize, so on a Member's view that address is
  // the viewer's and never where an authorization would send replies: only the organization's
  // email is shown there.
  const contacts = (submission?.replyContacts ?? []).filter(
    (contact) => !member || contact.source === 'organization_email',
  );
  const chosen =
    contacts.find((contact) => contact.source === replySource) ?? contacts[0] ?? undefined;
  const replyContact = authorization?.replyContact ?? chosen?.address;
  const recipients = submission?.recipients;
  const destination = authorization?.destination ?? submission?.destination?.address ?? '';
  const sink =
    attempt !== undefined
      ? attempt.sinkApplied
        ? attempt.to[0]
        : undefined
      : recipients?.sinkApplied === true
        ? recipients.to[0]
        : undefined;

  const attachments = files.map((file) => ({
    key: file.key,
    fileName: file.fileName,
    byteSize: file.byteSize,
  }));
  const email: ShownEmail | undefined = (() => {
    if (authorization !== undefined && attempt !== undefined) {
      return {
        from: fromLine(authorization.fromDisplayName, authorization.fromAddress),
        to: attempt.to,
        cc: attempt.cc,
        ...(attempt.replyTo !== undefined ? { replyTo: attempt.replyTo } : {}),
        ...(attempt.sinkApplied ? { inPlaceOf: authorization.destination } : {}),
        subject: authorization.transmittalSubject,
        body: authorization.transmittalBody,
        attachments,
      };
    }
    if (authorization !== undefined) {
      return {
        from: fromLine(authorization.fromDisplayName, authorization.fromAddress),
        to: recipients?.to ?? [authorization.destination],
        cc: recipients?.cc ?? [],
        replyTo: recipients?.replyTo ?? authorization.replyContact,
        ...(recipients?.sinkApplied === true ? { inPlaceOf: authorization.destination } : {}),
        subject: authorization.transmittalSubject,
        body: authorization.transmittalBody,
        attachments,
      };
    }
    const transmittal = submission?.transmittal;
    if (transmittal === undefined) return undefined;
    return {
      from:
        recipients !== undefined
          ? fromLine(recipients.fromDisplayName, recipients.fromAddress)
          : '',
      to: recipients?.to ?? [destination],
      // The served recipients name the default contact; the one chosen here takes its place. A
      // Member with no organization email shown sees no copy: the served one is their own email.
      cc:
        recipients !== undefined && recipients.cc.length > 0
          ? chosen !== undefined
            ? [chosen.address]
            : member
              ? []
              : recipients.cc
          : [],
      ...(chosen !== undefined ? { replyTo: chosen.address } : {}),
      ...(recipients?.sinkApplied === true ? { inPlaceOf: destination } : {}),
      subject: transmittal.subject,
      body: transmittal.body,
      attachments,
    };
  })();

  // ── The card's rows (the review, the authorized card and sending) ──────────────────────────
  // The agency's own words (CCM-R1-SUBMIT-HANDOFF, G3): served only where they are the rule's.
  const instructions = submission?.instructions;
  // The To row's source (CCM-ST2-SUBMIT-CITE): the citation the backend serves for the address,
  // where it serves one; otherwise the line drawn before it, from the form's own words.
  const citation = submission?.destination?.citation;
  const emailQuote = instructions?.channels.find((channel) => channel.channel === 'email')?.quote;
  const formSource = instructions?.formSource;
  const emailItem = items.find((item) => item.reads.some((read) => read.field === 'email'));
  const lettersNote =
    instructions?.replies !== undefined
      ? lettersLine(
          jurisdiction,
          instructions.replies.text,
          emailItem?.number,
          formValue(items, 'email'),
        )
      : undefined;
  const feeItem =
    submission?.feeStatement?.itemNumber !== undefined
      ? items.find((item) => item.number === submission.feeStatement?.itemNumber)
      : undefined;
  const signedSlots = signed?.slots.filter((slot) => slot.standing === 'signed') ?? [];
  // CCM-R1-SIGN-OUTSIDE-SEND-B (frames 111 and 112): a copy signed outside Givmo is the form's
  // execution, as the read names it. Its signers read as the organization states them, each on the
  // day it gives (a civil date, never moved by the viewer's zone), and its statement (W7) takes the
  // e-signature line's place: the line is about Givmo's signature (D-CCM-53 4).
  const copyExecution =
    submission?.execution?.kind === 'executed_copy' ? submission.execution.execution : undefined;
  // Before an authorization, today's checklist; once one exists, the checklist it kept (an
  // authorization is only ever made with every item settled, so one that kept none still reads as
  // every item settled, with no counts: CCM-R1-SUBMIT-SENT-SUMMARY).
  const itemName = (index: number) =>
    checklistItems.find((item) => item.attachmentIndex === index)?.item ?? '';
  const openItems =
    authorization === undefined
      ? checklistItems.filter((item) => item.status === 'open').map((item) => item.item)
      : (authorization.checklist ?? [])
          .filter((entry) => entry.status === 'open')
          .map((entry) => itemName(entry.attachmentIndex));
  const attached =
    authorization === undefined
      ? checklistItems.filter((item) => item.status === 'attached').length
      : keptChecklist?.attached;
  const rows: CardRow[] = [
    ...(destination.length > 0
      ? [
          {
            key: 'to',
            label: CARD_ROW_LABELS.to,
            value: (
              <>
                <span className="text-s14 text-text">{destination}</span>
                {citation !== undefined ? (
                  <SourceLine testId={`${PREFIX}-to-quote`}>
                    {toRowCitationLine(
                      citation,
                      sourceDateWording({
                        ruleSourceDate: citation.sourceDate,
                        ruleSourceDateBasis: citation.sourceDateBasis,
                      }),
                      sourceChangeWording({
                        ruleSourceChangeFoundOn: citation.sourceChangeFoundOn,
                        ruleSourceDateBasis: citation.sourceDateBasis,
                        ruleSourceChangeOnLineSource: citation.sourceChangeOnCitedSource,
                      }),
                    )}
                    {citation.sourceUrl !== undefined && officialSourceLink(citation.sourceUrl)}
                  </SourceLine>
                ) : (
                  emailQuote !== undefined && (
                    <SourceLine testId={`${PREFIX}-to-quote`}>
                      {toRowQuoteLine(emailQuote, formSource?.checkedOn)}
                      {formSource !== undefined && officialSourceLink(formSource.sourceUrl)}
                    </SourceLine>
                  )
                )}
              </>
            ),
          },
        ]
      : []),
    ...(signedFormTitle !== undefined
      ? [
          {
            key: 'filing',
            label: CARD_ROW_LABELS.filing,
            value: (
              <>
                <span>{signedFormTitle}</span>
                <SourceLine>
                  {ein !== undefined ? `${organization} · EIN ${ein}` : organization}
                </SourceLine>
                {periodLine !== undefined && <SourceLine>{periodLine}</SourceLine>}
              </>
            ),
          },
        ]
      : []),
    ...(submission?.feeStatement !== undefined
      ? [
          {
            key: 'fee',
            label: CARD_ROW_LABELS.fee,
            value: (
              <>
                <span>{feeValue(submission.feeStatement)}</span>
                <SourceLine>
                  {enteredLine(
                    submission.feeStatement.itemNumber,
                    makerName(
                      personName,
                      feeItem?.answer?.answeredByUserId,
                      feeItem?.answer?.answeredByGivmoDesk,
                    ),
                    feeItem?.answer?.answeredAt !== undefined
                      ? shortDateIn(feeItem.answer.answeredAt, zone)
                      : undefined,
                  )}
                </SourceLine>
              </>
            ),
          },
        ]
      : []),
    ...(signedSlots.length > 0
      ? [
          {
            key: 'signature',
            label: CARD_ROW_LABELS.signature,
            value: (
              <>
                {signedSlots.map((slot) => (
                  <div key={slot.slotId} className="flex flex-col gap-2">
                    <span>
                      {signedByLine(
                        personName?.(slot.signerUserId),
                        shortDateIn(slot.standingActAt, zone),
                      )}
                    </span>
                    <SourceLine>{capacityLine(slot.capacityText)}</SourceLine>
                  </div>
                ))}
                {submission?.signature?.factualLineApplies === true &&
                  submission.signature.factualLine !== undefined && (
                    <span className="pt-2" {...testIdProp(`${PREFIX}-factual-line`)}>
                      {submission.signature.factualLine}
                    </span>
                  )}
              </>
            ),
          },
        ]
      : copyExecution !== undefined
        ? [
            {
              key: 'signature',
              label: CARD_ROW_LABELS.signature,
              value: (
                <>
                  {copyExecution.signers.map((signer, index) => (
                    <div key={`${signer.name}-${index}`} className="flex flex-col gap-2">
                      <span>
                        {signedByLine(signer.name, displayComplianceDate(signer.signedOn))}
                      </span>
                      <SourceLine>{capacityLine(signer.capacity)}</SourceLine>
                    </div>
                  ))}
                  <span className="pt-2" {...testIdProp(`${PREFIX}-signed-outside`)}>
                    {copyExecution.statement}
                  </span>
                </>
              ),
            },
          ]
        : []),
    ...(checklistItems.length > 0
      ? [
          {
            key: 'checklist',
            label: CARD_ROW_LABELS.checklist,
            value: (
              <>
                <span>{openItems.length > 0 ? stillOpenLine(openItems) : EVERY_ITEM_LINE}</span>
                <SourceLine>
                  {attached !== undefined && notNeeded !== undefined && (
                    <>{checklistCountsLine(attached, notNeeded)} · </>
                  )}
                  <Link
                    to={COMPLIANCE_ROUTES.filingForm(filingId)}
                    search={yearSearch}
                    className="underline underline-offset-2 hover:text-text-secondary"
                    {...testIdProp(`${PREFIX}-see-the-checklist`)}
                  >
                    {SEE_THE_CHECKLIST}
                  </Link>
                </SourceLine>
              </>
            ),
          },
        ]
      : []),
  ];

  // ── The reply block ──────────────────────────────────────────────────────────────────────────
  const options: ReplyChoiceOption[] = contacts.map((contact) => ({
    source: contact.source,
    address: contact.address,
    label:
      contact.source === 'organization_email'
        ? organizationEmailLabel(organization, emailFromProfile(items))
        : YOUR_SIGN_IN_EMAIL,
  }));
  const fixedReplyLine = ((): string | undefined => {
    if (authorization !== undefined)
      return repliesGoToLine(jurisdiction, authorization.replyContact);
    if (member || options.length !== 1) {
      return member && chosen !== undefined
        ? repliesGoToLine(jurisdiction, chosen.address)
        : undefined;
    }
    const only = options[0];
    if (only === undefined) return undefined;
    return only.source === 'authorizer_email'
      ? repliesGoToSignInLine(jurisdiction, only.address)
      : repliesGoToLine(jurisdiction, only.address);
  })();
  const replyBlock =
    options.length > 0 || authorization !== undefined ? (
      <ReplyChoice
        heading={repliesGoTo(jurisdiction)}
        options={options}
        value={chosen?.source}
        onChange={setReplySource}
        {...(fixedReplyLine !== undefined ? { fixedLine: fixedReplyLine } : {})}
        {...(lettersNote !== undefined ? { note: lettersNote } : {})}
      />
    ) : null;

  // ── The decision, and the record ─────────────────────────────────────────────────────────────
  const nameOf = (userId: string | undefined) =>
    userId !== undefined ? personName?.(userId) : undefined;

  // ── What came back (CCM-R1-RESULTS-WEB; REVIEW-30 sections 1, 2 and 4) ─────────────────────────
  // Attach the confirmation opens the record form the panel opens (slice 1b), for this case's year.
  const recordTarget: RecordTarget | undefined =
    obligation === undefined || target === undefined
      ? undefined
      : target.kind === 'renewal'
        ? {
            kind: 'renewal-cycle',
            cycle: target.cycle,
            receiptKind: receiptKindFor(
              obligation.obligationClass,
              isYearKeyed(target.cycle.cycleKey),
            ),
          }
        : {
            kind: 'obligation',
            obligation,
            receiptKind: receiptKindFor(obligation.obligationClass, false),
          };
  const openSendView = (view: typeof SEND_AGAIN_SEARCH | typeof SEND_YOURSELF_SEARCH) =>
    void navigate({
      to: COMPLIANCE_ROUTES.filingSend(filingId),
      search: { ...view, ...yearSearch },
    });
  const backToTheFiling = () => void navigate({ to: COMPLIANCE_ROUTES.filing(filingId) });
  // Frame 66's line, the ordinary wait: the agency's own words for what comes back, where served.
  const waitLine = whatHappensNextLine(
    jurisdiction,
    instructions?.response?.text,
    instructions?.replies?.text,
  );
  /** The list of one send's reports and records (section 1). */
  const deliveryList = (rows: readonly SubmissionEvidence[]) => (
    <DeliveryList
      rows={deliveryListRows(rows, jurisdiction)}
      jurisdiction={jurisdiction}
      zone={zone}
      nameOf={nameOf}
      artifactName={artifactName}
      canAnswer={submission?.abilities.answerEvidence === true}
      onAnswer={(record) =>
        setAnswering({
          kind: 'record',
          row: record,
          recorder: nameOf(record.recordedByUserId),
          own: viewerUserId !== undefined && record.recordedByUserId === viewerUserId,
        })
      }
    />
  );
  /**
   * "What happens next" after a send, by what came back, its step's act leading (a1–a8); with the
   * confirmation on file, nothing (a9). Attach the confirmation puts the record form in its place.
   * After a send the organization made itself (U1), a rejection's line keeps its first sentence:
   * sending that again takes no authorization in Givmo.
   */
  const resultsNext = (
    followThrough: SubmissionFollowThrough | undefined,
    selfSent = false,
  ): ReactNode => {
    const step = followThrough !== undefined ? nextStepOf(followThrough.state) : 'wait';
    if (step === undefined) return null;
    // CCM-R1-DESK-WEB (desk-15, desk-16): on a filing Givmo's desk has, the desk sends it again or
    // prepares the corrected one, so its own line stands in the organization's step (Q4), with
    // "Review and authorize" where it needs the organization's authorization. After a bounce the
    // assignment stays dispatched and the read offers the authorization, with no need served:
    // W3c's own words say it (desk-15).
    if (deskHeld && (step === 'send-again' || step === 'prepare-corrected')) {
      const authorizeAgain = step === 'send-again' && submission?.abilities.authorize === true;
      const line = authorizeAgain ? w3(W3C_NEED) : deskLine;
      if (line === undefined) return null;
      const deskActs: ReactNode[] = [];
      if ((authorizeAgain || needsAuthorization(desk)) && submission?.abilities.authorize === true)
        deskActs.push(
          <ResultsAct
            key="authorize"
            primary
            label={N13}
            onClick={() => openSendView(SEND_AGAIN_SEARCH)}
            testId={`${PREFIX}-desk-review-and-authorize`}
          />,
        );
      if (submission?.abilities.recordEvidence === true)
        deskActs.push(
          <ResultsAct
            key="record"
            label={recordAnswerLabel(jurisdiction)}
            onClick={() => setRecordingAnswer(true)}
            testId={`${PREFIX}-record-answer-open`}
          />,
        );
      return (
        <WhatHappensNext
          title={WHAT_HAPPENS_NEXT}
          line={line}
          back={BACK_TO_THE_FILING}
          onBack={backToTheFiling}
          acts={deskActs}
        />
      );
    }
    if (confirming && recordTarget !== undefined) {
      const period =
        recordTarget.kind === 'renewal-cycle' ? periodLineOf(recordTarget.cycle) : undefined;
      return (
        <div className="border-t border-border-subtle pt-14">
          <RecordFilingForm
            target={recordTarget}
            {...(period !== undefined ? { periodLine: period } : {})}
            {...(row?.confirmationFrom !== undefined ? { answeredBy: row.confirmationFrom } : {})}
            initial="confirmed"
            onDone={() => setConfirming(false)}
          />
        </div>
      );
    }
    const acts: ReactNode[] = [];
    if (step === 'send-again') {
      acts.push(
        <ResultsAct
          key="again"
          primary
          label={SEND_IT_AGAIN}
          onClick={() => openSendView(SEND_AGAIN_SEARCH)}
          testId={`${PREFIX}-send-it-again`}
        />,
      );
      if (submission?.instructions !== undefined)
        acts.push(
          <ResultsAct
            key="yourself"
            label={SEND_IT_YOURSELF_INSTEAD}
            onClick={() => openSendView(SEND_YOURSELF_SEARCH)}
            testId={`${PREFIX}-again-yourself`}
          />,
        );
    }
    if (step === 'attach-confirmation' && canWrite === true && recordTarget !== undefined)
      acts.push(
        <ResultsAct
          key="attach"
          primary
          label={ATTACH_THE_CONFIRMATION}
          onClick={() => setConfirming(true)}
          testId={`${PREFIX}-attach-confirmation`}
        />,
      );
    if (step === 'prepare-corrected')
      acts.push(
        <ResultsAct
          key="prepare"
          primary
          label={PREPARE_THE_CORRECTED_FILING}
          onClick={() =>
            void navigate({
              to: COMPLIANCE_ROUTES.filingForm(filingId),
              ...withChosenYear(chosenCycleId),
            })
          }
          testId={`${PREFIX}-prepare-corrected`}
        />,
      );
    if (submission?.abilities.recordEvidence === true)
      acts.push(
        <ResultsAct
          key="record"
          label={recordAnswerLabel(jurisdiction)}
          onClick={() => setRecordingAnswer(true)}
          testId={`${PREFIX}-record-answer-open`}
        />,
      );
    return (
      <WhatHappensNext
        title={WHAT_HAPPENS_NEXT}
        line={
          step === 'send-again'
            ? YOU_CAN_SEND_IT_AGAIN
            : step === 'attach-confirmation'
              ? receivingIsntAcceptingLine(jurisdiction)
              : step === 'prepare-corrected'
                ? selfSent
                  ? PREPARE_THE_CORRECTED_FILING_FIRST
                  : PREPARE_THE_CORRECTED_FILING_LINE
                : waitLine
        }
        back={BACK_TO_THE_FILING}
        onBack={backToTheFiling}
        acts={acts}
      />
    );
  };
  /**
   * Section 2: an email the email service didn't take (frame 68), or one someone recorded as never
   * arriving (frame 70), that the email service later reported its address took: "Reported as
   * sent", the report, the served refusal, and no send — the backend would refuse it as already
   * sent. A Compliance Owner or a Global Admin can say it didn't reach the agency (d1).
   *
   * CCM-R1-RESULTS-WEB-REPORTED-SENT-STAYS (D-CCM-79 (j)): the card stays whatever became of its
   * authorization (the stage, `reportedSent`), with no Revoke, and continues as the email card does
   * once a person records what came back: the agency's answer with a5's or a7's next step, and the
   * confirmation's line, with nothing left to say it didn't reach the agency (the GO's Q1 and Q2).
   */
  const reportedAsSent = (attempt: SubmissionAttempt, first: string): ReactNode => {
    if (submission === undefined) return null;
    const send: ShownSend = { attemptId: attempt.attemptId };
    const rows = evidenceAbout(submission.evidence, send);
    const followThrough = followThroughOf(submission, send);
    const later = laterReportLine(rows, jurisdiction, zone);
    // Where a person recorded the agency's answer about it, or the filing's confirmation is on file,
    // that is what's known now (the email card's line, approved), and its next step follows.
    const answered =
      followThrough !== undefined &&
      (followThrough.state === 'acknowledged' ||
        followThrough.state === 'rejected' ||
        followThrough.state === 'correction_requested' ||
        followThrough.state === 'confirmed')
        ? knownNowLine(followThrough, rows, jurisdiction, zone, nameOf)
        : undefined;
    const next = answered !== undefined ? resultsNext(followThrough) : null;
    const refusal = submission.readiness.find(
      (reason) => reason.code === SUBMISSION_REFUSALS.alreadySent,
    )?.message;
    const reports = takingReports(rows);
    return (
      <RecordCard title={REPORTED_AS_SENT} testId={`${PREFIX}-reported-as-sent`}>
        <RecordLines
          first={first}
          rest={[
            ...(later !== undefined ? [later] : []),
            ...(answered !== undefined ? [answered] : []),
            ...(refusal !== undefined ? [refusal] : []),
          ]}
        />
        {next}
        {answered === undefined &&
          reports.length > 0 &&
          (submission.abilities.answerEvidence ? (
            <div>
              <ResultsAct
                label={itDidntReachLabel(jurisdiction)}
                onClick={() => setAnswering({ kind: 'report', rows: reports })}
                testId={`${PREFIX}-didnt-reach`}
              />
            </div>
          ) : (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-record-owner-line`)}
            >
              {ONLY_OWNERS_RECORD}
            </p>
          ))}
        {deliveryList(rows)}
      </RecordCard>
    );
  };
  const sinkNote =
    sink !== undefined && destination.length > 0 && !isOutcome(stage ?? { kind: 'review' })
      ? sinkLine(sink, destination, replyContact)
      : undefined;
  const statement = submission?.statement;
  const echoes: ComplianceSubmissionAuthorize | undefined =
    submission?.package !== undefined &&
    submission.transmittal !== undefined &&
    submission.destination !== undefined &&
    submission.feeStatement !== undefined &&
    submission.feeStatement.kind !== 'unknown' &&
    statement !== undefined &&
    chosen !== undefined
      ? {
          package_sha256: submission.package.packageSha256,
          transmittal_sha256: submission.transmittal.transmittalSha256,
          destination: submission.destination.address,
          fee_statement_kind: submission.feeStatement.kind,
          statement_id: statement.statementId,
          reply_contact_source: chosen.source,
          reply_contact: chosen.address,
        }
      : undefined;
  // Frame 74's act: the same echoes, by frame 74's words — offered only to a person whose own AI
  // is connected here and may write Compliance (the host's fact), and only where they're served.
  const connectorStatement = submission?.connectorStatement;
  const aiOffer =
    connectedAiCanSend === true && connectorStatement !== undefined && !member && !deskHeld;
  const aiEchoes: ComplianceSubmissionAuthorize | undefined =
    echoes !== undefined && connectorStatement !== undefined
      ? { ...echoes, statement_id: connectorStatement.statementId }
      : undefined;
  const holds = [...new Set((submission?.readiness ?? []).map((reason) => reason.message))];
  // A dismissal says nothing, and an account the check can't be met by reads W1 instead.
  const actFailure =
    authorizeAndSend.error !== null &&
    !isStepUpCancelled(authorizeAndSend.error) &&
    !failedForWantOfAPasskey(authorizeAndSend.error)
      ? authorizeAndSend.error
      : null;
  const aiActFailure =
    authorizeForAi.error !== null &&
    !isStepUpCancelled(authorizeForAi.error) &&
    !failedForWantOfAPasskey(authorizeForAi.error)
      ? authorizeForAi.error
      : null;

  const authorizationCard = (
    section: ReactNode,
    face: { title?: string; lead?: readonly string[] } = {},
  ) => (
    <AuthorizationCard
      title={face.title ?? (member ? AUTHORIZATION : YOUR_AUTHORIZATION)}
      {...(face.lead !== undefined ? { lead: face.lead } : {})}
      {...(sinkNote !== undefined ? { sink: sinkNote } : {})}
    >
      <CardRows rows={rows} />
      {replyBlock}
      {section}
    </AuthorizationCard>
  );

  // "Send it yourself instead" (frames 62, 71, 73): the same page's other face, by its search.
  const yourselfLink =
    submission?.instructions !== undefined && !deskHeld ? (
      <div>
        <QuietButton
          onClick={() =>
            void navigate({
              to: COMPLIANCE_ROUTES.filingSend(filingId),
              search: { ...SEND_YOURSELF_SEARCH, ...yearSearch },
            })
          }
          testId={`${PREFIX}-yourself`}
        >
          {SEND_IT_YOURSELF_INSTEAD}
        </QuietButton>
      </div>
    ) : null;

  // Frame 62: "Send it yourself instead" at the act's left, "Let your AI send this" at its right.
  const actLinks =
    yourselfLink !== null || aiOffer ? (
      <div className="flex flex-wrap items-center justify-between gap-x-16 gap-y-8">
        {yourselfLink ?? <span />}
        {aiOffer && (
          <QuietButton onClick={() => setAiView(true)} testId={`${PREFIX}-let-your-ai`}>
            {LET_YOUR_AI_SEND_THIS}
          </QuietButton>
        )}
      </div>
    ) : null;
  // W1 and the passkey offer, for whichever act the person pressed.
  const checkNotice = passkeyCheck.shown
    ? { notice: cannotMeetCheckToSendLine(organizationName) }
    : {};
  // "Not now" brings the button back with W1 standing; a passkey set up brings it back without, and
  // nothing runs until the person presses. An offer that can't work here shuts the act, W1 standing.
  const passkeyOffer = passkeyCheck.offer !== undefined ? { inPlaceOfAct: passkeyCheck.offer } : {};
  // With no offer to make, or an offer that can't work here, a press could only meet the same
  // refusal again.
  const checkShut = passkeyCheck.actShut;

  const decision = ((): ReactNode => {
    if (submission === undefined || stage === undefined) return null;
    // A send under way from this page, or one the read finds pending: frame 65.
    const pending = inFlight !== null ? inFlight : stage.kind === 'sending' ? stage.attempt : null;
    if (pending !== null) {
      const at = 'at' in pending ? pending.at : pending.committedAt;
      const by = 'at' in pending ? pending.byUserId : pending.dispatchedByUserId;
      // The organization's AI's send under way (W-b): said as its, never as a press here.
      const byAi = !('at' in pending) && sentByConnector(pending);
      // CCM-R1-DESK-WEB: Givmo's desk's send under way names the desk (N12), under the desk case's
      // own act (D-CCM-67's "Authorize").
      const byDesk = !('at' in pending) && pending.dispatchedThrough === 'desk';
      const label =
        'at' in pending
          ? pending.label
          : byAi
            ? SEND_IT_NOW
            : byDesk
              ? AUTHORIZE
              : pending.ordinal === 1
                ? AUTHORIZE_AND_SEND
                : SEND_IT;
      const time = whenOf(at, zone).time;
      return authorizationCard(
        <SendingSection
          title={sendingTitle(jurisdiction)}
          line={
            byAi ? aiStartedLine(time, nameOf(by)) : startedLine(time, byDesk ? N12 : nameOf(by))
          }
          busyLabel={label}
        />,
      );
    }
    switch (stage.kind) {
      case 'review':
        return authorizationCard(
          member ? (
            <MemberSection
              line={onlyOwnersAuthorizeLine(
                organization,
                complianceAuthorizers?.map((person) => person.name),
              )}
              after={yourselfLink}
            />
          ) : aiView && aiOffer && connectorStatement !== undefined ? (
            <ActSection
              heading={LET_YOUR_AI_SEND_THIS}
              lines={[
                aiCanSendLine(submission.package?.entries.length ?? files.length, jurisdiction),
                aiWorksUntilLine(connectorStatement.lifetimeSeconds, new Date(), zone),
              ]}
              statement={connectorStatement.text}
              label={AUTHORIZE_MY_AI_TO_SEND_IT}
              actTestId={`${PREFIX}-authorize-ai`}
              disabled={
                !submission.ready ||
                !submission.abilities.authorize ||
                aiEchoes === undefined ||
                checkShut
              }
              pending={gate.inFlight}
              holds={submission.ready ? [] : holds}
              failure={aiActFailure}
              onAct={() => {
                if (aiEchoes === undefined || acting.current) return;
                acting.current = true;
                authorizeForAi.mutate(aiEchoes);
              }}
              after={
                <div>
                  <QuietButton onClick={() => setAiView(false)} testId={`${PREFIX}-send-it-myself`}>
                    {SEND_IT_MYSELF_INSTEAD}
                  </QuietButton>
                </div>
              }
              {...checkNotice}
              {...passkeyOffer}
            />
          ) : statement !== undefined ? (
            <ActSection
              statement={statement.text}
              disabled={
                !submission.ready ||
                !submission.abilities.authorize ||
                echoes === undefined ||
                checkShut
              }
              pending={gate.inFlight}
              holds={submission.ready ? [] : holds}
              failure={actFailure}
              onAct={() => {
                if (echoes === undefined || acting.current) return;
                acting.current = true;
                authorizeAndSend.mutate(echoes);
              }}
              after={actLinks}
              {...(deskHeld
                ? {
                    label: AUTHORIZE,
                    // Going again after a bounce (desk-15's N13): W3c, as the results card says it,
                    // never the desk's own W5 (review-143's F3).
                    lines: goingAgain ? [w3(W3C_NEED)] : deskLine !== undefined ? [deskLine] : [],
                  }
                : {})}
              {...checkNotice}
              {...passkeyOffer}
            />
          ) : (
            <CardSection testId={`${PREFIX}-holds`}>
              {holds.map((line) => (
                <p key={line} className="text-s13 leading-h15 text-text-secondary">
                  {line}
                </p>
              ))}
            </CardSection>
          ),
        );
      case 'authorized': {
        if (deskHeld) {
          // CCM-R1-DESK-WEB: the desk sends what the organization authorized; its act here is
          // Revoke. Where the desk needs a new authorization while this one is in force (desk-10),
          // N9 says why.
          return authorizationCard(
            <SendAgainActs
              lines={
                needsAuthorization(desk) && deskLine !== undefined
                  ? [deskLine, worksUntilLine(stage.authorization.expiresAt, zone), N9]
                  : [W4, worksUntilLine(stage.authorization.expiresAt, zone)]
              }
              label={SEND_IT}
              canSend={false}
              canRevoke={submission.abilities.revoke}
              sending={false}
              revoking={revoke.isPending}
              failure={againFailure}
              onSend={() => undefined}
              onRevoke={() => revoke.mutate(stage.authorization)}
            />,
          );
        }
        const forAi = stage.authorization.connectorDispatchAllowed;
        if (forAi && submission.abilities.connectorDispatch) {
          // Frame 75: authorized for the viewer's own AI, which may send it now.
          return authorizationCard(
            <SendAgainActs
              label={SEND_IT_NOW}
              canSend={submission.abilities.dispatch}
              canRevoke={submission.abilities.revoke}
              sending={sendAgain.isPending}
              revoking={revoke.isPending}
              failure={againFailure}
              onSend={() =>
                sendAgain.mutate({ authorization: stage.authorization, label: SEND_IT_NOW })
              }
              onRevoke={() => setRevoking(stage.authorization)}
            />,
            {
              title: AUTHORIZED_FOR_YOUR_AI,
              lead: [
                authorizedForYourAiLine(stage.authorization.expiresAt, zone),
                WHAT_YOU_TELL_YOUR_AI,
              ],
            },
          );
        }
        // M4: authorized and not sent, its refusal in the server's words; W-a where its author
        // let their AI send it.
        return authorizationCard(
          <SendAgainActs
            lines={[
              forAi
                ? aiAuthorizedByLine(
                    nameOf(stage.authorization.authorizedByUserId),
                    stage.authorization.expiresAt,
                    zone,
                  )
                : worksUntilLine(stage.authorization.expiresAt, zone),
            ]}
            label={SEND_IT}
            canSend={submission.abilities.dispatch}
            canRevoke={submission.abilities.revoke}
            sending={sendAgain.isPending}
            revoking={revoke.isPending}
            failure={againFailure}
            onSend={() => sendAgain.mutate({ authorization: stage.authorization, label: SEND_IT })}
            onRevoke={() => revoke.mutate(stage.authorization)}
          />,
        );
      }
      case 'sent': {
        const send: ShownSend = { attemptId: stage.attempt.attemptId };
        const rows = evidenceAbout(submission.evidence, send);
        const followThrough = followThroughOf(submission, send);
        return (
          <SentRecord
            authorization={stage.authorization}
            attempt={stage.attempt}
            jurisdiction={jurisdiction}
            zone={zone}
            nameOf={nameOf}
            knownNow={
              followThrough !== undefined
                ? knownNowLine(followThrough, rows, jurisdiction, zone, nameOf)
                : undefined
            }
            next={resultsNext(followThrough)}
            list={deliveryList(rows)}
          />
        );
      }
      case 'refused': {
        const first = refusedLine(whenOf(stage.attempt.committedAt, zone), jurisdiction);
        const rows = evidenceAbout(submission.evidence, { attemptId: stage.attempt.attemptId });
        if (deskHeld)
          // CCM-R1-DESK-WEB (desk-12): the desk tries again itself; the organization's act is
          // Revoke.
          return (
            <RecordCard title={NOT_SENT} testId={`${PREFIX}-refused`}>
              <RecordLines
                first={first}
                rest={[W4, worksUntilLine(stage.authorization.expiresAt, zone)]}
              />
              <SendAgainActs
                label={TRY_AGAIN}
                canSend={false}
                canRevoke={submission.abilities.revoke}
                sending={false}
                revoking={revoke.isPending}
                failure={againFailure}
                onSend={() => undefined}
                onRevoke={() => revoke.mutate(stage.authorization)}
              />
              {deliveryList(rows)}
            </RecordCard>
          );
        return (
          <RecordCard title={NOT_SENT} testId={`${PREFIX}-refused`}>
            <RecordLines
              first={first}
              rest={[TRYING_AGAIN_LINE, worksUntilLine(stage.authorization.expiresAt, zone)]}
            />
            <SendAgainActs
              label={TRY_AGAIN}
              canSend={submission.abilities.dispatch}
              canRevoke={submission.abilities.revoke}
              sending={sendAgain.isPending}
              revoking={revoke.isPending}
              failure={againFailure}
              onSend={() =>
                sendAgain.mutate({ authorization: stage.authorization, label: TRY_AGAIN })
              }
              onRevoke={() => revoke.mutate(stage.authorization)}
              after={IF_IT_HAPPENS_AGAIN}
            />
            {deliveryList(rows)}
          </RecordCard>
        );
      }
      case 'uncertain': {
        const attemptSink = stage.attempt.sinkApplied ? stage.attempt.to[0] : undefined;
        return (
          <RecordCard title={OUTCOME_UNCERTAIN} testId={`${PREFIX}-uncertain`}>
            <RecordLines first={uncertainLine(whenOf(stage.attempt.committedAt, zone))} rest={[]} />
            <UncertainQuestion
              question={didItArriveLine(stage.attempt.replyTo, attemptSink)}
              hint={subjectHintLine(organization, ein)}
              canRecord={submission.abilities.reconcile}
              memberLine={ONLY_OWNERS_RECORD}
              onFinding={(basis) => setFinding({ attemptId: stage.attempt.attemptId, basis })}
            />
          </RecordCard>
        );
      }
      case 'notSent': {
        const attemptSink = stage.attempt.sinkApplied ? stage.attempt.to[0] : undefined;
        const first = recordedNeverArrivedLine(
          nameOf(stage.attempt.reconciledByUserId),
          shortDateIn(stage.attempt.reconciledAt, zone),
          stage.attempt.replyTo,
          attemptSink,
        );
        const rows = evidenceAbout(submission.evidence, { attemptId: stage.attempt.attemptId });
        return (
          <RecordCard title={NOT_SENT} testId={`${PREFIX}-not-sent`}>
            <RecordLines
              first={first}
              rest={[YOU_CAN_SEND_IT_NOW, worksUntilLine(stage.authorization.expiresAt, zone)]}
            />
            <SendAgainActs
              label={SEND_IT}
              canSend={submission.abilities.dispatch}
              canRevoke={submission.abilities.revoke}
              sending={sendAgain.isPending}
              revoking={revoke.isPending}
              failure={againFailure}
              onSend={() =>
                sendAgain.mutate({ authorization: stage.authorization, label: SEND_IT })
              }
              onRevoke={() => revoke.mutate(stage.authorization)}
            />
            {deliveryList(rows)}
          </RecordCard>
        );
      }
      case 'reportedSent': {
        // b1 and b2: frame 70's or 68's own line, then "Reported as sent".
        const attemptSink = stage.attempt.sinkApplied ? stage.attempt.to[0] : undefined;
        const first =
          stage.attempt.state === 'refused'
            ? refusedLine(whenOf(stage.attempt.committedAt, zone), jurisdiction)
            : recordedNeverArrivedLine(
                nameOf(stage.attempt.reconciledByUserId),
                shortDateIn(stage.attempt.reconciledAt, zone),
                stage.attempt.replyTo,
                attemptSink,
              );
        return reportedAsSent(stage.attempt, first);
      }
      default:
        // A send in a state this build doesn't know: the rows, and no act.
        return authorizationCard(null);
    }
  })();

  const findingAttempt =
    finding !== undefined
      ? submission?.attempts.find((candidate) => candidate.attemptId === finding.attemptId)
      : undefined;
  const findingWords =
    finding !== undefined && findingAttempt !== undefined
      ? findingDialog(finding.basis, {
          day: shortDateIn(findingAttempt.committedAt, zone),
          replyContact: findingAttempt.replyTo,
          sink: findingAttempt.sinkApplied ? findingAttempt.to[0] : undefined,
          jurisdiction,
        })
      : undefined;

  let body;
  if (orgError) {
    body = (
      <EmptyState
        testId={`${PREFIX}-org-error`}
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Couldn’t load your organization"
        description={serverMessage(orgFailure, 'Please try again.')}
      />
    );
  } else if (failed !== undefined && failed !== null) {
    body = isModuleUnavailable(failed) ? (
      <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
    ) : (
      <p className="text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-error`)}>
        {serverMessage(failed, LOAD_FAILED)}
      </p>
    );
  } else if (
    !settled ||
    copyRead.pending ||
    copyOnly ||
    submission === undefined ||
    stage === undefined
  ) {
    body = <Skeleton className="h-[480px] w-full" {...testIdProp(`${PREFIX}-loading`)} />;
  } else if (handoffShown && submission.instructions !== undefined) {
    // Sending it yourself (frames 79–80). Once recorded, the page shows the package as recorded,
    // its counts from the checklist the record kept, never today's.
    const handoff = newestHandoff(submission);
    const recordedPackage = handoff?.content === 'package' ? handoff : undefined;
    const handoffNotNeeded =
      recordedPackage !== undefined
        ? recordedPackage.checklist !== undefined
          ? checklistRecordCounts(recordedPackage.checklist).notNeeded
          : undefined
        : todaysNotNeeded;
    const signers: HandoffSigner[] =
      copyExecution !== undefined
        ? copyExecution.signers.map((signer, index) => ({
            key: `copy-${index}`,
            name: signer.name,
            day: displayComplianceDate(signer.signedOn),
            capacity: signer.capacity,
          }))
        : signedSlots.map((slot) => ({
            key: slot.slotId,
            name: personName?.(slot.signerUserId),
            day: shortDateIn(slot.standingActAt, zone),
            capacity: slot.capacityText,
          }));
    const handoffDigest =
      recordedPackage !== undefined
        ? recordedPackage.packageSha256
        : submission.package?.packageSha256;
    // N-H1 (approved, D-CCM-40): back to Givmo's email, for a filing Givmo can email, reached by
    // "Send it yourself instead". It opens the review, where nothing is emailed until someone
    // authorizes it — after an email that never arrived too (a3), by the review "Send it again"
    // opens.
    const emailInstead =
      yourself &&
      handoff === undefined &&
      (goingAgain || panelSendAction(submission) === 'review') ? (
        <div>
          <QuietButton
            onClick={() =>
              goingAgain
                ? openSendView(SEND_AGAIN_SEARCH)
                : void navigate({
                    to: COMPLIANCE_ROUTES.filingSend(filingId),
                    ...withChosenYear(chosenCycleId),
                  })
            }
            testId={`${PREFIX}-email-instead`}
          >
            {HAVE_GIVMO_EMAIL_IT_INSTEAD}
          </QuietButton>
        </div>
      ) : undefined;
    // U1 (the GO's Q2): what came back after the organization's own send, under frame 81's line,
    // with the email card's blocks and approved lines.
    const handoffSend: ShownSend | undefined =
      handoff !== undefined ? { handoffId: handoff.handoffId } : undefined;
    const handoffRows =
      handoffSend !== undefined ? evidenceAbout(submission.evidence, handoffSend) : [];
    const handoffFollowThrough =
      handoffSend !== undefined ? followThroughOf(submission, handoffSend) : undefined;
    const handoffKnown =
      handoffFollowThrough !== undefined
        ? knownNowLine(handoffFollowThrough, handoffRows, jurisdiction, zone, nameOf)
        : undefined;
    const handoffResults =
      handoffSend !== undefined ? (
        <>
          {handoffKnown !== undefined && (
            <p
              className="-mt-4 text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-handoff-known`)}
            >
              {handoffKnown}
            </p>
          )}
          {resultsNext(handoffFollowThrough, true)}
          {deliveryList(handoffRows)}
        </>
      ) : undefined;
    body = (
      <HandoffBody
        jurisdiction={jurisdiction}
        instructions={submission.instructions}
        leadReason={handoffLeadReason(submission)}
        files={recordedPackage !== undefined ? fromBound(recordedPackage.package) : composedFiles}
        notNeeded={handoffNotNeeded}
        packageSha256={handoffDigest}
        fee={submission.feeStatement}
        feeEnteredBy={makerName(
          personName,
          feeItem?.answer?.answeredByUserId,
          feeItem?.answer?.answeredByGivmoDesk,
        )}
        feeEnteredOn={
          feeItem?.answer?.answeredAt !== undefined
            ? shortDateIn(feeItem.answer.answeredAt, zone)
            : undefined
        }
        checklistPage={servedChecklistPage(preparationRead.data)}
        signers={signers}
        factualLine={
          copyExecution !== undefined
            ? copyExecution.statement
            : submission.signature?.factualLineApplies === true
              ? submission.signature.factualLine
              : undefined
        }
        recorded={handoff}
        recordedBy={handoff !== undefined ? nameOf(handoff.recordedByUserId) : undefined}
        canRecord={submission.abilities.recordHandoff}
        downloading={download.isPending}
        recording={recordHandoff.isPending}
        recordFailure={recordHandoff.error}
        {...(emailInstead !== undefined ? { emailInstead } : {})}
        {...(handoffResults !== undefined ? { results: handoffResults } : {})}
        onOpen={(file) => void openVersion(file.documentId, file.versionId)}
        onDownload={() => {
          if (handoffDigest !== undefined) download.mutate(handoffDigest);
        }}
        onRecord={(record) => recordHandoff.mutate(record)}
        onResetRecord={() => recordHandoff.reset()}
      />
    );
  } else {
    // Row 7's sentence sits under the package until sending starts (O4 (A)).
    const sentence = !isOutcome(stage) && inFlight === null;
    body = (
      <div className="grid items-start gap-32 lg:grid-cols-[minmax(0,1fr)_440px]">
        <div className="flex min-w-0 flex-col gap-32">
          {files.length > 0 && (
            <SendPackageSection
              files={files}
              jurisdiction={jurisdiction}
              notNeeded={notNeeded}
              totalBytes={totalBytes}
              sentence={sentence}
              onOpen={(file) => void openVersion(file.documentId, file.versionId)}
            />
          )}
          {email !== undefined && <SendEmailSection email={email} />}
        </div>
        {/* At 390 the decision comes last, so everything is passed on the way to the button. */}
        <div className={`flex min-w-0 flex-col gap-16 ${STICKY_COLUMN_CLASSES}`}>{decision}</div>
      </div>
    );
  }

  return (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-24 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.filingSend)}
    >
      <div className="flex flex-col gap-12">
        <Trail
          items={[
            { label: 'Filings', to: COMPLIANCE_ROUTES.filings },
            { label: filingTitle, to: COMPLIANCE_ROUTES.filing(filingId) },
          ]}
          testId={`${PREFIX}-trail`}
        />
        <PageHeader
          title={jurisdiction.length > 0 ? sendToTitle(jurisdiction) : ''}
          {...(signedFormTitle !== undefined
            ? {
                subtitle:
                  periodLine !== undefined ? `${signedFormTitle} · ${periodLine}` : signedFormTitle,
              }
            : {})}
          {...(packageDigest !== undefined && !handoffShown
            ? {
                actions: (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                    loading={download.isPending}
                    onClick={() => download.mutate(packageDigest)}
                    testId={`${PREFIX}-download`}
                  >
                    {DOWNLOAD_THE_PACKAGE}
                  </Button>
                ),
              }
            : {})}
        />
      </div>
      {body}
      {gate.dialog}
      <RevokeForAiDialog
        open={revoking !== undefined}
        pending={revoke.isPending}
        failure={revoking !== undefined ? revoke.error : null}
        onConfirm={() => {
          if (revoking !== undefined) revoke.mutate(revoking);
        }}
        onClose={() => {
          revoke.reset();
          setRevoking(undefined);
        }}
      />
      <FindingDialog
        words={findingWords}
        pending={reconcile.isPending}
        failure={reconcile.error}
        onConfirm={() => {
          if (finding !== undefined) reconcile.mutate(finding);
        }}
        onClose={() => {
          reconcile.reset();
          setFinding(undefined);
        }}
      />
      <RecordAnswerDialog
        open={recordingAnswer}
        jurisdiction={jurisdiction}
        sends={submission !== undefined ? sendsThatWentOut(submission, zone, nameOf) : []}
        today={records?.today}
        pending={recordEvidence.isPending}
        failure={recordEvidence.error}
        onRecord={(body) => recordEvidence.mutate(body)}
        onAttachConfirmation={() => {
          recordEvidence.reset();
          setRecordingAnswer(false);
          setConfirming(true);
        }}
        onClose={() => {
          recordEvidence.reset();
          setRecordingAnswer(false);
        }}
      />
      <AnswerDialog
        target={answering}
        jurisdiction={jurisdiction}
        pending={answerEvidence.isPending}
        failure={answerEvidence.error}
        onAnswer={(body) => {
          if (answering === undefined) return;
          // A report the dialog opened on may be answered since: tried again after the next one's
          // answer failed, it would be answered twice, which is refused (one answer per row). So only
          // the reports still standing in the read as it is now.
          const standing = new Set(
            standingEvidence(submission?.evidence ?? []).map((row) => row.evidenceId),
          );
          answerEvidence.mutate({
            evidenceIds:
              answering.kind === 'report'
                ? answering.rows
                    .map((report) => report.evidenceId)
                    .filter((evidenceId) => standing.has(evidenceId))
                : [answering.row.evidenceId],
            body,
          });
        }}
        onClose={() => {
          answerEvidence.reset();
          setAnswering(undefined);
        }}
      />
    </div>
  );
}

/**
 * Frame 66: what was sent, what that does and doesn't show, what happens next, and the record;
 * and what came back since (CCM-R1-RESULTS-WEB, a1–a9): the line saying what's known now under
 * the card's own, "What happens next" by it, and the list of reports and answers.
 */
function SentRecord({
  authorization,
  attempt,
  jurisdiction,
  zone,
  nameOf,
  knownNow,
  next,
  list,
}: {
  authorization: SubmissionAuthorization;
  attempt: SubmissionAttempt;
  jurisdiction: string;
  zone: string | undefined;
  nameOf: (userId: string | undefined) => string | undefined;
  knownNow: string | undefined;
  next: ReactNode;
  list: ReactNode;
}) {
  const when = whenOf(attempt.committedAt, zone);
  const sink = attempt.sinkApplied ? attempt.to[0] : undefined;
  // Frame 77: the organization's AI sent it, for the person it acts for.
  const byAi = sentByConnector(attempt);
  // CCM-R1-DESK-WEB (desk-13): Givmo's desk sent it (N10, N11). Where this environment's test
  // address took it, the first line says so, as for anyone's send.
  const byDesk = attempt.dispatchedThrough === 'desk';
  const sender = nameOf(attempt.dispatchedByUserId);
  const first = byDesk
    ? sink !== undefined
      ? sentToSinkLine(sink, when, authorization.destination)
      : n10(authorization.destination, when.day, when.time)
    : byAi
      ? sink !== undefined
        ? sentToSinkByAiLine(sink, when, authorization.destination, sender)
        : sentByAiLine(authorization.destination, when, sender)
      : sink !== undefined
        ? sentToSinkLine(sink, when, authorization.destination)
        : sentByEmailLine(authorization.destination, when);
  const basis = attempt.reconciliationBasis;
  // A send recorded as sent says who found it and how, never that the email service took it.
  const second =
    basis === 'copy_received' || basis === 'provider_activity'
      ? reconciledSentLine(
          nameOf(attempt.reconciledByUserId),
          shortDateIn(attempt.reconciledAt, zone),
          basis,
          attempt.replyTo,
          sink,
        )
      : acceptedLine(jurisdiction, attempt.replyTo, attempt.sinkApplied);
  return (
    <RecordCard title={sentTitle(jurisdiction)} testId={`${PREFIX}-sent`}>
      <RecordLines first={first} rest={knownNow !== undefined ? [second, knownNow] : [second]} />
      {byAi && attempt.connectorChatRequest !== undefined && (
        <AiAskedBox
          authorized={aiAuthorizedOnLine(
            nameOf(authorization.authorizedByUserId),
            whenOf(authorization.authorizedAt, zone),
          )}
          asked={aiAskedLine(
            nameOf(authorization.authorizedByUserId),
            attempt.connectorChatRequest,
          )}
        />
      )}
      {next}
      {list}
      <SendingRecord
        title={SENDING_RECORD}
        lines={[
          authorizedRecordLine(authorization, nameOf(authorization.authorizedByUserId), zone),
          byDesk ? N11 : byAi ? sentByConnectedAiLine(sender) : sentFromGivmoLine(sender),
        ]}
        quiet={[
          recipientsRecordLine(attempt, authorization.fromDisplayName),
          digestsRecordLine(authorization.packageSha256, attempt.providerMessageId),
        ]}
        denial={
          basis === 'copy_received' || basis === 'provider_activity'
            ? recordedSendDenial(jurisdiction)
            : sendingRecordDenial(jurisdiction)
        }
      />
    </RecordCard>
  );
}
